import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { acces as api } from '../services/api';
import './Objectifs.css';
import './Stock.css';
import './ReferentielMarche.css';
import './Creances.css';   // cr-btn
import './ReferentielVentesAdd.css';
import './AdminAcces.css';

// ─── Administration ▸ Accès (2026-10-07) ─────────────────────────────────────
// Qui voit quoi dans A97 BI : par personne (compte Microsoft), un rôle, des
// PLAQUES (Auto Antilles, Auto Métropole, Groupe), des BLOCS (VN, VO, APV…) et,
// au besoin, une restriction à certaines sociétés de ces plaques. Réservé aux
// administrateurs ; chaque enregistrement est historisé et vaut tout de suite
// (l'habilitation est relue à chaque requête, pas au prochain login).
//
// Une plaque donnée entière couvre aussi les sociétés qu'on y ajoutera ; une
// liste de sociétés, non. Les blocs sont contrôlés sur les données, pas
// seulement dans le menu.

const NOUVELLE = { email: '', nom: '', prenom: '', role: 'lecteur', actif: true, branches: ['*'], societes: ['*'], metiers: ['*'], ia: 'lecture', export_excel: false, agent: false, rapports: [], nouvelle: true };
// Droit « Analyse IA » (2026-10-21), contrôlé aussi par le serveur.
const DROITS_IA = [{ c: 'aucune', l: 'Aucune' }, { c: 'lecture', l: 'Lecture' }, { c: 'generation', l: 'Génération' }];
const libIa = (c) => (DROITS_IA.find((d) => d.c === c) || {}).l || '—';
const tout = (l) => (l || []).includes('*');
const dateFr = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '—');

// Bascule segmentée à choix multiple, « Toutes » en tête (st-seg).
function SegMulti({ options, valeur, onChange, libelleTout, disabled }) {
  const tous = tout(valeur);
  const basculer = (code) => {
    const l = tous ? [] : valeur.filter((c) => c !== '*');
    const n = l.includes(code) ? l.filter((c) => c !== code) : [...l, code];
    onChange(n.length === 0 || n.length === options.length ? ['*'] : n);
  };
  return (
    <div className="st-seg ac-seg" role="group">
      <button type="button" className={tous ? 'on' : ''} disabled={disabled} onClick={() => onChange(['*'])}>{libelleTout}</button>
      {options.map((o) => (
        <button type="button" key={o.code} disabled={disabled} className={!tous && valeur.includes(o.code) ? 'on' : ''} onClick={() => basculer(o.code)}>{o.nom}</button>
      ))}
    </div>
  );
}

export default function AdminAcces() {
  const [d, setD] = useState(null);
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');
  const [brouillon, setBrouillon] = useState(null);
  const [attente, setAttente] = useState(false);
  const [recherche, setRecherche] = useState('');

  const charger = useCallback(() => api.charger()
    .then((r) => setD(r.data))
    .catch((e) => setErreur(e.response?.data?.error || 'Erreur de chargement')), []);
  useEffect(() => { charger(); }, [charger]);

  const noms = useMemo(() => {
    if (!d) return {};
    const m = (l) => Object.fromEntries(l.map((x) => [x.code, x.nom]));
    return { branches: m(d.branches), metiers: m(d.metiers), societes: m(d.societes) };
  }, [d]);
  const libelle = (l, ref, toutes) => (tout(l) ? toutes : (l || []).map((c) => noms[ref][c] || c).join(', '));

  const ouvrir = (h) => { setBrouillon({ ...h }); setMessage(''); setErreur(''); };
  const maj = (k, v) => setBrouillon((b) => ({ ...b, [k]: v }));
  const enregistrer = async () => {
    setAttente(true); setErreur('');
    try {
      await api.enregistrer(brouillon);
      setMessage(`Accès de ${brouillon.prenom || ''} ${brouillon.nom || brouillon.email} enregistré.`.replace(/\s+/g, ' '));
      setBrouillon(null); await charger();
    } catch (e) { setErreur(e.response?.data?.error || 'Enregistrement impossible'); }
    finally { setAttente(false); }
  };

  // Téléphone perdu ou changé : nouveau QR code à la prochaine connexion.
  const reinitialiser2fa = async () => {
    if (!window.confirm('Réinitialiser la double authentification ? Un nouveau QR code sera demandé à la prochaine connexion.')) return;
    setAttente(true); setErreur('');
    try {
      await api.reinitialiser2fa(brouillon.id);
      setMessage(`Double authentification de ${brouillon.prenom || ''} ${brouillon.nom || brouillon.email} réinitialisée.`.replace(/\s+/g, ' '));
      setBrouillon(null); await charger();
    } catch (e) { setErreur(e.response?.data?.error || 'Réinitialisation impossible'); }
    finally { setAttente(false); }
  };

  // Sociétés proposables : celles des plaques choisies.
  const societesPlaques = useMemo(() => {
    if (!d || !brouillon) return [];
    return d.branches.filter((b) => tout(brouillon.branches) || brouillon.branches.includes(b.code))
      .map((b) => ({ branche: b, societes: d.societes.filter((s) => s.branche_code === b.code) }))
      .filter((g) => g.societes.length > 0);
  }, [d, brouillon]);
  const restreint = brouillon && !tout(brouillon.societes);
  const basculerSociete = (code) => {
    const l = brouillon.societes.filter((c) => c !== '*');
    maj('societes', l.includes(code) ? l.filter((c) => c !== code) : [...l, code]);
  };

  const q = recherche.trim().toLowerCase();
  const lignes = (d?.habilitations || []).filter((h) => !q
    || `${h.prenom || ''} ${h.nom || ''} ${h.email}`.toLowerCase().includes(q));
  const moi = brouillon && d && brouillon.email.toLowerCase() === String(d.moi).toLowerCase();

  return (
    <main className="ob-page rm-page">
      <div className="ob-head">
        <h1>Accès</h1>
        <p className="ob-sub">Qui voit quoi dans A97 BI : pour chaque compte Microsoft, les plaques et les blocs autorisés, et au besoin certaines sociétés seulement. Une modification vaut immédiatement.</p>
      </div>
      {erreur && <div className="ob-erreur">⚠️ {erreur}</div>}
      {message && <div className="ob-ok">{message}</div>}
      {!d ? <div className="vt-skel" /> : (
        <div className="vt-card">
          <div className="vt-card-h">
            <h2>Habilitations <span className="ac-nb">{d.habilitations.filter((h) => h.actif).length} actives</span></h2>
            <div className="st-tools">
              <input className="ac-recherche" placeholder="Rechercher…" value={recherche} onChange={(e) => setRecherche(e.target.value)} />
              <button type="button" className="cr-btn ghost" onClick={() => ouvrir(NOUVELLE)}>+ Ajouter une personne</button>
            </div>
          </div>
          <div className="vt-card-b vt-tbl-wrap">
            <table className="vt-tbl">
              <thead><tr><th className="l">Personne</th><th className="l">Rôle</th><th className="l">Plaques</th><th className="l">Blocs</th><th className="l">Sociétés</th><th className="l">Analyse IA</th><th className="l">Export Excel</th><th className="l">Double auth.</th><th className="l">Dernière connexion</th></tr></thead>
              <tbody>
                {lignes.map((h) => (
                  <tr key={h.id} className={`tbl-clic${brouillon?.id === h.id ? ' actif' : ''}${h.actif ? '' : ' ac-inactif'}`} onClick={() => ouvrir(h)}>
                    <td className="l"><b>{[h.prenom, h.nom].filter(Boolean).join(' ') || '—'}</b><br /><small className="ob-faint">{h.email}</small></td>
                    <td className="l">{h.actif ? (h.role === 'admin' ? 'Administrateur' : 'Lecteur') : <span className="ac-off">Désactivé</span>}</td>
                    <td className="l">{libelle(h.branches, 'branches', 'Toutes')}</td>
                    <td className="l">{libelle(h.metiers, 'metiers', 'Tous')}</td>
                    <td className="l">{tout(h.societes) ? 'Toutes celles des plaques' : `${h.societes.length} société${h.societes.length > 1 ? 's' : ''}`}</td>
                    <td className="l">{h.ia === 'aucune' ? <span className="ob-faint">Aucune</span> : libIa(h.ia)}</td>
                    <td className="l">{h.export_excel ? 'Oui' : <span className="ob-faint">Non</span>}</td>
                    <td className="l">{h.totp_actif ? 'Active' : <span className="ob-faint">À configurer</span>}</td>
                    <td className="l">{dateFr(h.derniere_connexion)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {brouillon && d && (
        <div className="vt-card">
          <div className="vt-card-h">
            <h2>{brouillon.nouvelle ? 'Nouvelle personne' : [brouillon.prenom, brouillon.nom].filter(Boolean).join(' ') || brouillon.email}</h2>
            <div className="st-tools">
              <button type="button" className="cr-btn" disabled={attente || !brouillon.email || (restreint && brouillon.societes.length === 0)} onClick={enregistrer}>Enregistrer</button>
              <button type="button" className="cr-btn ghost" onClick={() => setBrouillon(null)}>Fermer</button>
            </div>
          </div>
          <div className="vt-card-b">
            <table className="vt-tbl ac-form"><tbody>
              <tr><td className="l">E-mail Microsoft</td><td className="l"><input className="rva-input" value={brouillon.email} onChange={(e) => maj('email', e.target.value)} placeholder="prenom.nom@societe.com" /></td></tr>
              <tr><td className="l">Nom</td><td className="l"><input className="rva-input" value={brouillon.nom || ''} onChange={(e) => maj('nom', e.target.value)} /></td></tr>
              <tr><td className="l">Prénom</td><td className="l"><input className="rva-input" value={brouillon.prenom || ''} onChange={(e) => maj('prenom', e.target.value)} /></td></tr>
              <tr><td className="l">Rôle</td><td className="l">
                <div className="st-seg ac-seg">
                  <button type="button" className={brouillon.role !== 'admin' ? 'on' : ''} disabled={moi} onClick={() => maj('role', 'lecteur')}>Lecteur</button>
                  <button type="button" className={brouillon.role === 'admin' ? 'on' : ''} onClick={() => maj('role', 'admin')}>Administrateur</button>
                </div>
                <span className="hint ac-hint">l'administrateur modifie les objectifs, les référentiels, les accès et lance les analyses IA</span>
              </td></tr>
              <tr><td className="l">Plaques</td><td className="l">
                <SegMulti options={d.branches} valeur={brouillon.branches} libelleTout="Toutes" onChange={(v) => maj('branches', v)} />
                <span className="hint ac-hint">une plaque entière couvre aussi les sociétés qu'on y ajoutera</span>
              </td></tr>
              <tr><td className="l">Blocs</td><td className="l">
                <SegMulti options={d.metiers} valeur={brouillon.metiers} libelleTout="Tous" onChange={(v) => maj('metiers', v)} />
                <span className="hint ac-hint">contrôlés aussi sur les données : un rapport hors de ses blocs répond « accès refusé »</span>
              </td></tr>
              {(d.reserves || []).length > 0 && (
                <tr><td className="l">Rapports réservés</td><td className="l">
                  <div className="ac-reserves">
                    {d.reserves.map((r) => (
                      <label key={r.slug} className="ac-case">
                        <input type="checkbox" checked={(brouillon.rapports || []).includes(r.slug)}
                          onChange={() => maj('rapports', (brouillon.rapports || []).includes(r.slug) ? brouillon.rapports.filter((x) => x !== r.slug) : [...(brouillon.rapports || []), r.slug])} />
                        {' '}{r.titre}{r.branche ? <small className="ob-faint"> · {r.branche}</small> : null}
                      </label>
                    ))}
                  </div>
                  <span className="hint ac-hint">visibles des seules personnes cochées, en plus de la plaque et du bloc Finance</span>
                </td></tr>
              )}
              <tr><td className="l">Sociétés</td><td className="l">
                <div className="st-seg ac-seg">
                  <button type="button" className={!restreint ? 'on' : ''} onClick={() => maj('societes', ['*'])}>Toutes celles des plaques</button>
                  <button type="button" className={restreint ? 'on' : ''} onClick={() => restreint || maj('societes', [])}>Certaines seulement</button>
                </div>
                {restreint && brouillon.societes.length === 0 && <span className="hint ac-hint">cochez au moins une société</span>}
                {restreint && (
                  <div className="ac-societes">
                    {societesPlaques.map((g) => (
                      <div key={g.branche.code} className="ac-groupe">
                        <div className="ac-groupe-t">{g.branche.nom}</div>
                        {g.societes.map((s) => (
                          <label key={s.code} className="ac-case">
                            <input type="checkbox" checked={brouillon.societes.includes(s.code)} onChange={() => basculerSociete(s.code)} /> {s.nom}
                          </label>
                        ))}
                      </div>
                    ))}
                    
                  </div>
                )}
              </td></tr>
              <tr><td className="l">Accès</td><td className="l">
                <div className="st-seg ac-seg">
                  <button type="button" className={brouillon.actif !== false ? 'on' : ''} onClick={() => maj('actif', true)}>Actif</button>
                  <button type="button" className={brouillon.actif === false ? 'on' : ''} disabled={moi} onClick={() => maj('actif', false)}>Désactivé</button>
                </div>
                {moi && <span className="hint ac-hint">vous ne pouvez pas retirer votre propre accès administrateur</span>}
              </td></tr>
              <tr><td className="l">Analyse IA</td><td className="l">
                <div className="st-seg ac-seg">
                  {DROITS_IA.map((d) => <button key={d.c} type="button" className={(brouillon.ia || 'lecture') === d.c ? 'on' : ''} onClick={() => maj('ia', d.c)}>{d.l}</button>)}
                </div>
                <span className="hint ac-hint">aucune : bouton masqué · lecture : analyses déjà faites · génération : peut en lancer (coût d'IA)</span>
              </td></tr>
              <tr><td className="l">Export Excel</td><td className="l">
                <div className="st-seg ac-seg">
                  <button type="button" className={!brouillon.export_excel ? 'on' : ''} onClick={() => maj('export_excel', false)}>Non</button>
                  <button type="button" className={brouillon.export_excel ? 'on' : ''} onClick={() => maj('export_excel', true)}>Oui</button>
                </div>
                <span className="hint ac-hint">clic droit sur un bloc : Exporter en Excel, Copier en image ; le bloc tel qu'affiché, dans son périmètre ; chaque export est tracé ci-dessous</span>
              </td></tr>
              <tr><td className="l">Agent Claude</td><td className="l">
                <div className="st-seg ac-seg">
                  <button type="button" className={!brouillon.agent ? 'on' : ''} onClick={() => maj('agent', false)}>Non</button>
                  <button type="button" className={brouillon.agent ? 'on' : ''} onClick={() => maj('agent', true)}>Oui</button>
                </div>
                <span className="hint ac-hint">connecteur A97 BI dans Claude (résumé de la veille, concessions sous alias) ; chaque appel est tracé</span>
              </td></tr>
              {brouillon.id && (
                <tr><td className="l">Double authentification</td><td className="l">
                  {brouillon.totp_actif
                    ? <>Active depuis le {dateFr(brouillon.totp_active_le)} <button type="button" className="cr-btn ghost" disabled={attente} onClick={reinitialiser2fa}>Réinitialiser</button></>
                    : <span className="ob-faint">À configurer à la prochaine connexion</span>}
                </td></tr>
              )}
            </tbody></table>
          </div>
        </div>
      )}

      {d && d.historique.length > 0 && (
        <div className="vt-card">
          <div className="vt-card-h"><h2>Dernières modifications</h2></div>
          <div className="vt-card-b vt-tbl-wrap">
            <table className="vt-tbl tbl-dense">
              <thead><tr><th className="l">Date</th><th className="l">Personne</th><th className="l">Modification</th><th className="l">Par</th></tr></thead>
              <tbody>
                {d.historique.map((r) => {
                  const a = r.avant || {}; const p = r.apres || {};
                  const champs = [['role', 'rôle'], ['actif', 'statut'], ['branches', 'plaques'], ['metiers', 'blocs'], ['societes', 'sociétés'], ['email', 'e-mail'], ['nom', 'nom'], ['prenom', 'prénom'], ['ia', 'analyse IA'], ['export_excel', 'export Excel'], ['agent', 'agent Claude'], ['rapports', 'rapports réservés'], ['double_authentification', 'double authentification réinitialisée']]
                    .filter(([k]) => JSON.stringify(a[k]) !== JSON.stringify(p[k])).map(([, l]) => l);
                  return (
                    <tr key={r.id}>
                      <td className="l">{new Date(r.revise_le).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}</td>
                      <td className="l">{r.email}</td>
                      <td className="l">{r.avant ? (champs.length ? champs.join(', ') : 'aucun changement') : 'création'}</td>
                      <td className="l">{r.revise_par}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {d && d.exports && d.exports.length > 0 && (
        <div className="vt-card">
          <div className="vt-card-h"><h2>Derniers exports</h2></div>
          <div className="vt-card-b vt-tbl-wrap">
            <table className="vt-tbl tbl-dense">
              <thead><tr><th className="l">Date</th><th className="l">Personne</th><th className="l">Rapport</th><th className="l">Bloc</th><th className="l">Format</th><th>Lignes</th></tr></thead>
              <tbody>
                {d.exports.map((x, i) => (
                  <tr key={i}>
                    <td className="l">{new Date(x.exporte_le).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}</td>
                    <td className="l">{x.email}</td><td className="l">{x.rapport}</td><td className="l">{x.tableau}</td><td className="l">{x.format === 'image' ? 'Image' : x.format === 'csv' ? 'CSV' : 'Excel'}</td><td>{x.lignes ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </main>
  );
}
